import { describeApiError } from '@brush/api-client';
import { ErrorNotice, TagPill } from '@brush/ui';
import { Plus, Shield, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useAddPlatformTag, useRemovePlatformTag } from '../queries';
import { draftBoardWarning } from '../tags/removal';
import { removalSentence, usedInLine } from '../tags/usage';

export function PlatformTagsPanel({
  tags,
  usage,
}: {
  tags: string[];
  /** How many tournaments carry each tag, from the server; null while it has not arrived. */
  usage: ReadonlyMap<string, number> | null;
}) {
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  // What the last removal said about Draft boards that named the tag. Kept until the next removal
  // or addition rather than cleared on a timer: it names rooms somebody has to go and tell.
  const [warning, setWarning] = useState<string | null>(null);
  const addTag = useAddPlatformTag();
  const removeTag = useRemovePlatformTag();

  const countOf = (tag: string) => usage?.get(tag);

  const add = () => {
    const clean = draft.trim();
    if (!clean || tags.some((t) => t.toLowerCase() === clean.toLowerCase())) return;
    setWarning(null);
    addTag.mutate(clean, { onSuccess: () => setDraft('') });
  };

  return (
    <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="border-b border-ink-800/10 px-6 py-4">
        <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
          <Shield className="size-4 text-gold-500" /> Platform tags
        </h2>
        <p className="mt-1 text-xs text-ink-800/60">
          Available as suggestions in every room. Rooms can apply them but can't remove them once
          applied.
        </p>
      </header>

      <div className="p-6">
        <ErrorNotice
          message={describeApiError(addTag.error) ?? describeApiError(removeTag.error)}
          className="mb-4"
        />

        {warning ? (
          <p
            role="status"
            className="mb-4 rounded-lg border border-gold-500/40 bg-gold-500/10 px-4 py-3 text-sm text-ink-950"
          >
            {warning}
          </p>
        ) : null}

        <div className="mb-4 flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
            }}
            placeholder="Add a platform tag (e.g. NLH, PLO, High Roller)"
            className="flex-1 rounded-lg border border-ink-800/15 px-3 py-2 text-sm outline-none focus:border-felt-500"
          />
          <button
            type="button"
            onClick={add}
            disabled={!draft.trim() || addTag.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
          >
            <Plus className="size-4" /> Add
          </button>
        </div>

        {tags.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-800/60">
            No platform tags yet. Add one above to get started.
          </p>
        ) : (
          <ul className="space-y-2">
            {tags.map((tag) => {
              const count = countOf(tag);
              return (
                <li
                  key={tag}
                  className="flex items-center justify-between rounded-lg border border-ink-800/10 p-3"
                >
                  <div className="flex items-center gap-3">
                    <TagPill label={tag} kind="platform" />
                    <span className="text-xs text-ink-800/60">
                      {usedInLine(count)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirming(tag)}
                    className="inline-flex items-center gap-1 text-xs text-ink-800/60 hover:text-chip-500"
                  >
                    <Trash2 className="size-3" /> Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirming ? (
        <ConfirmRemove
          tag={confirming}
          count={countOf(confirming)}
          pending={removeTag.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() =>
            removeTag.mutate(confirming, {
              onSuccess: (removal) => {
                setWarning(draftBoardWarning(confirming, removal.draftBoards));
                setConfirming(null);
              },
            })
          }
        />
      ) : null}
    </section>
  );
}

function ConfirmRemove({
  tag,
  count,
  pending,
  onCancel,
  onConfirm,
}: {
  tag: string;
  count: number | undefined;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="font-display text-lg font-semibold text-ink-950">Remove platform tag?</h3>
        <p className="mt-2 text-sm text-ink-800/70">
          <strong className="text-ink-950">&ldquo;{tag}&rdquo;</strong> {removalSentence(count)}
        </p>
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="flex-1 rounded-lg bg-chip-500 py-2 text-sm font-medium text-white hover:bg-chip-500/90 disabled:opacity-50"
          >
            {pending ? 'Removing…' : 'Remove tag'}
          </button>
        </div>
      </div>
    </div>
  );
}
